<template>
  <div>
    <!-- Hero Header -->
    <v-card class="hero-header mb-6" elevation="0">
      <div class="hero-content">
        <div class="hero-icon-wrapper">
          <v-icon size="48" color="white">mdi-tent</v-icon>
        </div>
        <div class="hero-text">
          <h1 class="hero-title">ข้อมูลลูกเสือวิสามัญอาชีวศึกษา</h1>
          <p class="hero-subtitle">
            ตำแหน่ง คุณวุฒิ และประสบการณ์ทางลูกเสือของบุคลากร
          </p>
        </div>
        <v-spacer></v-spacer>
        <v-chip
          v-if="!isSelfMode"
          class="mr-2"
          color="white"
          text-color="#2e7d32"
        >
          <v-icon left small>mdi-domain</v-icon>เพิ่มโดยวิทยาลัย
        </v-chip>
        <v-chip v-else class="mr-2" color="white" text-color="#2e7d32">
          <v-icon left small>mdi-account</v-icon>เพิ่มโดยตนเอง
        </v-chip>
        <v-btn
          class="add-btn"
          large
          depressed
          :disabled="isSelfMode && !scoutPeriodOpen"
          @click="personnel_scoutAdd()"
        >
          <v-icon left>mdi-plus</v-icon>
          เพิ่มข้อมูลลูกเสือ
        </v-btn>
      </div>
    </v-card>

    <!-- แจ้งเตือนช่วงเวลาปิดปรับข้อมูล (เฉพาะโหมด self-service) -->
    <v-alert
      v-if="isSelfMode && !scoutPeriodOpen"
      type="warning"
      outlined
      prominent
      border="left"
      class="mb-6"
    >
      <div class="font-weight-bold">ขณะนี้ปิดรับการปรับข้อมูลส่วนตัวด้านวุฒิลูกเสือ</div>
      <div>ท่านสามารถดูข้อมูลเดิมได้ แต่ยังไม่สามารถเพิ่ม/แก้ไข/ลบข้อมูลได้ในขณะนี้ กรุณาติดตามประกาศรอบเวลาถัดไป</div>
    </v-alert>

    <!-- Statistics Cards -->
    <v-row class="stats-row mb-6">
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-1" elevation="0">
          <div class="stat-icon">
            <v-icon color="white" size="28">mdi-file-document-multiple</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-number">{{ personnel_scouts.length }}</span>
            <span class="stat-label">ระเบียนทั้งหมด</span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-2" elevation="0">
          <div class="stat-icon">
            <v-icon color="white" size="28">mdi-account-star</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-number">{{
              countByLevel("ลูกเสือวิสามัญ")
            }}</span>
            <span class="stat-label">ลูกเสือวิสามัญ</span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-3" elevation="0">
          <div class="stat-icon">
            <v-icon color="white" size="28">mdi-microphone</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-number">{{ countInstructors }}</span>
            <span class="stat-label">เป็นวิทยากรลูกเสือ</span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-4" elevation="0">
          <div class="stat-icon">
            <v-icon color="white" size="28">mdi-medal</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-number">{{ countWithAwards }}</span>
            <span class="stat-label">มีเครื่องหมายเชิดชูเกียรติ</span>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Main Data Table -->
    <v-card class="main-table-card" elevation="0">
      <v-card-title class="table-header">
        <div class="header-left">
          <v-icon class="mr-3" color="#2e7d32">mdi-format-list-bulleted</v-icon>
          <span class="header-title">รายการข้อมูลลูกเสือวิสามัญ</span>
        </div>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="ค้นหา..."
          single-line
          hide-details
          dense
          outlined
          class="search-field"
          style="max-width: 350px"
        ></v-text-field>
      </v-card-title>

      <v-divider></v-divider>

      <v-data-table
        :loading="loading"
        :headers="headers"
        :items="personnel_scouts"
        :search="search"
        :items-per-page="10"
        class="modern-table"
        loading-text="กำลังโหลดข้อมูล..."
        no-data-text="ยังไม่มีข้อมูลลูกเสือวิสามัญ"
        no-results-text="ไม่พบผลลัพธ์ที่ค้นหา"
      >
        <template v-slot:item.scout_level="{ item }">
          <v-chip small dark :color="levelColor(item.scout_level)">
            <v-icon left small>mdi-shield-star-outline</v-icon>
            {{ item.scout_level || "-" }}
          </v-chip>
        </template>

        <template v-slot:item.scout_type="{ item }">
          <span>{{ item.scout_type || "-" }}</span>
        </template>

        <template v-slot:item.scout_position="{ item }">
          <span class="font-weight-medium">
            {{
              item.scout_position === "อื่น ๆ" && item.scout_position_other
                ? item.scout_position_other
                : item.scout_position || "-"
            }}
          </span>
        </template>

        <template v-slot:item.scout_qualification="{ item }">
          <v-chip small outlined color="#2e7d32">{{
            item.scout_qualification || "ไม่มีคุณวุฒิ"
          }}</v-chip>
        </template>

        <template v-slot:item.woodbadge_date="{ item }">
          <span v-if="item.woodbadge_date">{{
            formatThaiDate(item.woodbadge_date)
          }}</span>
          <span v-else class="grey--text">-</span>
        </template>

        <template v-slot:item.scout_experience_year="{ item }">
          <span v-if="item.scout_experience_year"
            >{{ item.scout_experience_year }} ปี</span
          >
          <span v-else class="grey--text">-</span>
        </template>

        <template v-slot:item.scout_instructor="{ item }">
          <v-chip
            x-small
            dark
            :color="item.scout_instructor === 'เป็น' ? 'success' : 'grey'"
          >
            {{ item.scout_instructor || "ไม่เป็น" }}
          </v-chip>
        </template>

        <template v-slot:item.scout_awards="{ item }">
          <div v-if="parseAwards(item.scout_awards).length">
            <v-chip
              v-for="(a, i) in parseAwards(item.scout_awards)"
              :key="i"
              x-small
              dark
              color="amber darken-2"
              class="mr-1 mb-1"
            >
              <v-icon x-small left>mdi-medal-outline</v-icon>{{ a }}
            </v-chip>
          </div>
          <span v-else class="grey--text">ไม่มี</span>
        </template>

        <template v-slot:item.source_type="{ item }">
          <v-chip
            x-small
            :color="item.source_type === 'self' ? 'blue' : 'teal'"
            dark
          >
            <v-icon x-small left>{{
              item.source_type === "self" ? "mdi-account" : "mdi-domain"
            }}</v-icon>
            {{ item.source_type === "self" ? "ตนเองเพิ่ม" : "วิทยาลัยเพิ่ม" }}
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                class="action-btn edit-btn mr-1"
                :disabled="isSelfMode && !scoutPeriodOpen"
                v-bind="attrs"
                v-on="on"
                @click.stop="personnel_scoutEdit(item.id_red)"
              >
                <v-icon small>mdi-pencil-outline</v-icon>
              </v-btn>
            </template>
            <span>แก้ไข</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                class="action-btn delete-btn"
                :disabled="isSelfMode && !scoutPeriodOpen"
                v-bind="attrs"
                v-on="on"
                @click.stop="personnel_scoutDelete(item.id_red)"
              >
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
            </template>
            <span>ลบ</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <!-- ============================================================ -->
    <!-- Add Dialog -->
    <!-- ============================================================ -->
    <v-dialog
      v-model="addpersonnel_scoutdialog"
      persistent
      max-width="900px"
      content-class="modern-dialog"
    >
      <v-card class="dialog-card">
        <v-card-title class="dialog-header add-header">
          <v-icon class="mr-3" color="white" size="28"
            >mdi-plus-circle-outline</v-icon
          >
          <span>เพิ่มข้อมูลลูกเสือวิสามัญ</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="addpersonnel_scoutdialog = false"
            ><v-icon>mdi-close</v-icon></v-btn
          >
        </v-card-title>

        <v-card-text class="dialog-content">
          <v-form ref="addpersonnel_scoutform" lazy-validation>
            <!-- Section 1: ระดับ / เหล่า / ตำแหน่ง -->
            <div class="form-section">
              <div class="section-title">
                <v-icon color="#2e7d32" class="mr-2"
                  >mdi-shield-account-outline</v-icon
                >
                ระดับ เหล่า และตำแหน่งทางลูกเสือ
              </div>
              <v-row>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="addpersonnel_scout.scout_level"
                    :items="scout_levels"
                    label="ระดับลูกเสือ"
                    outlined
                    dense
                    :rules="[v => !!v || 'กรุณาเลือกระดับลูกเสือ']"
                    prepend-inner-icon="mdi-shield-star-outline"
                    class="modern-input"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="addpersonnel_scout.scout_type"
                    :items="scout_types"
                    label="เหล่าลูกเสือ"
                    outlined
                    dense
                    clearable
                    prepend-inner-icon="mdi-flag-outline"
                    class="modern-input"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="addpersonnel_scout.scout_position"
                    :items="scout_positions"
                    label="ตำแหน่งทางลูกเสือ"
                    outlined
                    dense
                    :rules="[v => !!v || 'กรุณาเลือกตำแหน่งทางลูกเสือ']"
                    prepend-inner-icon="mdi-account-tie-outline"
                    class="modern-input"
                  ></v-select>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                  v-if="addpersonnel_scout.scout_position === 'อื่น ๆ'"
                >
                  <v-text-field
                    v-model="addpersonnel_scout.scout_position_other"
                    label="ระบุตำแหน่ง (กรณีเลือก 'อื่น ๆ')"
                    outlined
                    dense
                    prepend-inner-icon="mdi-pencil-outline"
                    class="modern-input"
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>

            <!-- Section 2: คุณวุฒิ -->
            <div class="form-section">
              <div class="section-title">
                <v-icon color="#1565c0" class="mr-2"
                  >mdi-certificate-outline</v-icon
                >
                คุณวุฒิทางลูกเสือ
              </div>
              <v-row>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="addpersonnel_scout.scout_qualification"
                    :items="scout_qualifications"
                    label="คุณวุฒิทางลูกเสือสูงสุดที่ได้รับ"
                    outlined
                    dense
                    prepend-inner-icon="mdi-file-certificate-outline"
                    class="modern-input"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-menu
                    v-model="menuWoodbadgeAdd"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="addpersonnel_scout.woodbadge_date"
                        label="วันที่ได้รับคุณวุฒิทางลูกเสือ"
                        prepend-inner-icon="mdi-calendar"
                        outlined
                        dense
                        readonly
                        clearable
                        v-bind="attrs"
                        v-on="on"
                        class="modern-input"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="addpersonnel_scout.woodbadge_date"
                      @input="menuWoodbadgeAdd = false"
                      locale="th"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
            </div>

            <!-- Section 3: การฝึกอบรมเพิ่มเติม -->
            <div class="form-section">
              <div class="section-title">
                <v-icon color="#fb8c00" class="mr-2">mdi-school-outline</v-icon>
                การฝึกอบรมเพิ่มเติม
              </div>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="addpersonnel_scout.scout_unit_training"
                    label="ผ่านการฝึกอบรมหลักสูตรเฉพาะหน่วย"
                    placeholder="เช่น ลูกเสือจิตอาสา, SFH, ช่อสะอาด"
                    outlined
                    dense
                    class="modern-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="addpersonnel_scout.scout_special_training"
                    label="ผ่านการฝึกอบรมหลักสูตรวิชาพิเศษ"
                    outlined
                    dense
                    class="modern-input"
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>

            <!-- Section 4: ประสบการณ์ / วิทยากร / เครื่องหมายเชิดชูเกียรติ -->
            <div class="form-section">
              <div class="section-title">
                <v-icon color="#c62828" class="mr-2">mdi-medal-outline</v-icon>
                ประสบการณ์และเกียรติประวัติ
              </div>
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="addpersonnel_scout.scout_experience_year"
                    label="ประสบการณ์ด้านลูกเสือ (ปี)"
                    type="number"
                    min="0"
                    outlined
                    dense
                    prepend-inner-icon="mdi-clock-outline"
                    class="modern-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="addpersonnel_scout.scout_instructor"
                    :items="['เป็น', 'ไม่เป็น']"
                    label="เป็นวิทยากรลูกเสือ"
                    outlined
                    dense
                    prepend-inner-icon="mdi-microphone-outline"
                    class="modern-input"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="4">
                  <v-autocomplete
                    v-model="addAwardsModel"
                    :items="scout_awards"
                    label="เครื่องหมายเชิดชูเกียรติ (เลือกได้หลายรายการ)"
                    outlined
                    dense
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    prepend-inner-icon="mdi-medal-outline"
                    class="modern-input"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="addpersonnel_scout.scout_comment"
                    label="หมายเหตุเพิ่มเติม"
                    outlined
                    dense
                    rows="2"
                    class="modern-input"
                  ></v-textarea>
                </v-col>
              </v-row>
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn
            text
            large
            @click="addpersonnel_scoutdialog = false"
            class="cancel-btn"
          >
            <v-icon left>mdi-close</v-icon>ยกเลิก
          </v-btn>
          <v-btn
            large
            depressed
            color="#2e7d32"
            dark
            @click="addpersonnel_scoutSubmit()"
            class="save-btn"
          >
            <v-icon left>mdi-content-save-outline</v-icon>บันทึกข้อมูล
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============================================================ -->
    <!-- Edit Dialog -->
    <!-- ============================================================ -->
    <v-dialog
      v-model="editpersonnel_scoutdialog"
      persistent
      max-width="900px"
      content-class="modern-dialog"
    >
      <v-card class="dialog-card">
        <v-card-title class="dialog-header edit-header">
          <v-icon class="mr-3" color="white" size="28"
            >mdi-pencil-outline</v-icon
          >
          <span>แก้ไขข้อมูลลูกเสือวิสามัญ</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="editpersonnel_scoutdialog = false"
            ><v-icon>mdi-close</v-icon></v-btn
          >
        </v-card-title>

        <v-card-text class="dialog-content">
          <v-form ref="editpersonnel_scoutform" lazy-validation>
            <div class="form-section">
              <div class="section-title">
                <v-icon color="#fb8c00" class="mr-2"
                  >mdi-shield-account-outline</v-icon
                >
                ระดับ เหล่า และตำแหน่งทางลูกเสือ
              </div>
              <v-row>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="editpersonnel_scout.scout_level"
                    :items="scout_levels"
                    label="ระดับลูกเสือ"
                    outlined
                    dense
                    :rules="[v => !!v || 'กรุณาเลือกระดับลูกเสือ']"
                    prepend-inner-icon="mdi-shield-star-outline"
                    class="modern-input"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="editpersonnel_scout.scout_type"
                    :items="scout_types"
                    label="เหล่าลูกเสือ"
                    outlined
                    dense
                    clearable
                    prepend-inner-icon="mdi-flag-outline"
                    class="modern-input"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="editpersonnel_scout.scout_position"
                    :items="scout_positions"
                    label="ตำแหน่งทางลูกเสือ"
                    outlined
                    dense
                    :rules="[v => !!v || 'กรุณาเลือกตำแหน่งทางลูกเสือ']"
                    prepend-inner-icon="mdi-account-tie-outline"
                    class="modern-input"
                  ></v-select>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                  v-if="editpersonnel_scout.scout_position === 'อื่น ๆ'"
                >
                  <v-text-field
                    v-model="editpersonnel_scout.scout_position_other"
                    label="ระบุตำแหน่ง (กรณีเลือก 'อื่น ๆ')"
                    outlined
                    dense
                    class="modern-input"
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>

            <div class="form-section">
              <div class="section-title">
                <v-icon color="#1565c0" class="mr-2"
                  >mdi-certificate-outline</v-icon
                >
                คุณวุฒิทางลูกเสือ
              </div>
              <v-row>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="editpersonnel_scout.scout_qualification"
                    :items="scout_qualifications"
                    label="คุณวุฒิทางลูกเสือสูงสุดที่ได้รับ"
                    outlined
                    dense
                    class="modern-input"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-menu
                    v-model="menuWoodbadgeEdit"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="editpersonnel_scout.woodbadge_date"
                        label="วันที่ได้รับคุณวุฒิทางลูกเสือ"
                        prepend-inner-icon="mdi-calendar"
                        outlined
                        dense
                        readonly
                        clearable
                        v-bind="attrs"
                        v-on="on"
                        class="modern-input"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="editpersonnel_scout.woodbadge_date"
                      @input="menuWoodbadgeEdit = false"
                      locale="th"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
            </div>

            <div class="form-section">
              <div class="section-title">
                <v-icon color="#fb8c00" class="mr-2">mdi-school-outline</v-icon>
                การฝึกอบรมเพิ่มเติม
              </div>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="editpersonnel_scout.scout_unit_training"
                    label="ผ่านการฝึกอบรมหลักสูตรเฉพาะหน่วย"
                    outlined
                    dense
                    class="modern-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="editpersonnel_scout.scout_special_training"
                    label="ผ่านการฝึกอบรมหลักสูตรวิชาพิเศษ"
                    outlined
                    dense
                    class="modern-input"
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>

            <div class="form-section">
              <div class="section-title">
                <v-icon color="#c62828" class="mr-2">mdi-medal-outline</v-icon>
                ประสบการณ์และเกียรติประวัติ
              </div>
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="editpersonnel_scout.scout_experience_year"
                    label="ประสบการณ์ด้านลูกเสือ (ปี)"
                    type="number"
                    min="0"
                    outlined
                    dense
                    class="modern-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="editpersonnel_scout.scout_instructor"
                    :items="['เป็น', 'ไม่เป็น']"
                    label="เป็นวิทยากรลูกเสือ"
                    outlined
                    dense
                    class="modern-input"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="4">
                  <v-autocomplete
                    v-model="editAwardsModel"
                    :items="scout_awards"
                    label="เครื่องหมายเชิดชูเกียรติ"
                    outlined
                    dense
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    class="modern-input"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="editpersonnel_scout.scout_comment"
                    label="หมายเหตุเพิ่มเติม"
                    outlined
                    dense
                    rows="2"
                    class="modern-input"
                  ></v-textarea>
                </v-col>
              </v-row>
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn
            text
            large
            @click="editpersonnel_scoutdialog = false"
            class="cancel-btn"
          >
            <v-icon left>mdi-close</v-icon>ยกเลิก
          </v-btn>
          <v-btn
            large
            depressed
            color="#fb8c00"
            dark
            @click="editpersonnel_scoutSubmit()"
            class="save-btn"
          >
            <v-icon left>mdi-content-save-outline</v-icon>บันทึกการแก้ไข
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog
      v-model="deletepersonnel_scoutdialog"
      persistent
      max-width="450px"
      content-class="modern-dialog"
    >
      <v-card class="dialog-card delete-dialog">
        <v-card-title class="dialog-header delete-header">
          <v-icon class="mr-3" color="white" size="28"
            >mdi-delete-alert-outline</v-icon
          >
          <span>ยืนยันการลบข้อมูล</span>
        </v-card-title>
        <v-card-text class="delete-content text-center">
          <div class="delete-icon-wrapper">
            <v-icon size="64" color="#e53935">mdi-alert-circle-outline</v-icon>
          </div>
          <div class="delete-message">
            คุณต้องการลบข้อมูลลูกเสือรายการนี้หรือไม่?
          </div>
          <v-chip
            class="delete-item-chip mt-3"
            color="#ffebee"
            text-color="#c62828"
            large
          >
            <v-icon left color="#c62828">mdi-tent</v-icon>
            {{ editpersonnel_scout.scout_level }} -
            {{ editpersonnel_scout.scout_position }}
          </v-chip>
          <div class="delete-warning mt-4">
            <v-icon small color="#ff9800" class="mr-1">mdi-alert</v-icon>
            การดำเนินการนี้ไม่สามารถย้อนกลับได้
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn
            text
            large
            @click="deletepersonnel_scoutdialog = false"
            class="cancel-btn"
          >
            <v-icon left>mdi-close</v-icon>ยกเลิก
          </v-btn>
          <v-btn
            large
            depressed
            color="#e53935"
            dark
            @click="deletepersonnel_scoutSubmit()"
            class="delete-confirm-btn"
          >
            <v-icon left>mdi-delete-outline</v-icon>ลบข้อมูล
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :timeout="snackbar.timeout"
      :color="snackbar.color"
      top
      right
      class="modern-snackbar"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-3" color="white">{{ snackbar.icon }}</v-icon>
        <span>{{ snackbar.text }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">ปิด</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  name: "PersonnelScout",
  props: {
    // เดียวกับ PersonnelEducation : รับข้อมูลบุคคลที่กำลังแก้ไขอยู่ (จากหน้า personnel_etc)
    editpersonnel_temporary: {
      type: [Object, Array],
      required: false,
      default: () => ({})
    },
    // 'college' = เรียกใช้จากหน้างานบุคลากรวิทยาลัย (ค่าเริ่มต้น)
    // 'self'    = เรียกใช้จากหน้า self-service ที่บุคคลล็อกอินด้วยเลขบัตรประชาชนเอง
    sourceType: {
      type: String,
      default: "college"
    }
  },

  data() {
    return {
      ApiKey: "HRvec2021",
      loading: true,
      search: "",
      addpersonnel_scoutdialog: false,
      editpersonnel_scoutdialog: false,
      deletepersonnel_scoutdialog: false,
      scoutPeriodOpen: true,
      menuWoodbadgeAdd: false,
      menuWoodbadgeEdit: false,
      snackbar: { show: false, color: "", timeout: 4000, icon: "", text: "" },

      headers: [
        {
          text: "ระดับลูกเสือ",
          align: "center",
          value: "scout_level",
          width: "150px"
        },
        { text: "เหล่า", align: "center", value: "scout_type", width: "110px" },
        { text: "ตำแหน่งทางลูกเสือ", align: "left", value: "scout_position" },
        {
          text: "คุณวุฒิ",
          align: "center",
          value: "scout_qualification",
          width: "160px"
        },
        {
          text: "วันที่ได้รับคุณวุฒิ",
          align: "center",
          value: "woodbadge_date",
          width: "130px"
        },
        {
          text: "ประสบการณ์",
          align: "center",
          value: "scout_experience_year",
          width: "100px"
        },
        {
          text: "วิทยากร",
          align: "center",
          value: "scout_instructor",
          width: "90px"
        },
        {
          text: "เครื่องหมายเชิดชูเกียรติ",
          align: "left",
          value: "scout_awards"
        },
        {
          text: "แหล่งที่มา",
          align: "center",
          value: "source_type",
          width: "120px"
        },
        {
          text: "จัดการ",
          align: "center",
          value: "actions",
          sortable: false,
          width: "100px"
        }
      ],

      personnel_scouts: [],
      addpersonnel_scout: {},
      editpersonnel_scout: {},

      // ===== Master data (คงที่ตามชีต "Master Data" ของไฟล์ต้นแบบ) =====
      scout_levels: [
        "ลูกเสือสำรอง",
        "ลูกเสือสามัญ",
        "ลูกเสือสามัญรุ่นใหญ่",
        "ลูกเสือวิสามัญ"
      ],
      scout_types: ["เหล่าเสนา", "เหล่าสมุทร", "เหล่าอากาศ"],
      scout_positions: [
        "ผู้อำนวยการลูกเสือโรงเรียน",
        "รองผู้อำนวยการลูกเสือโรงเรียน",
        "ผู้กำกับลูกเสือ(กลุ่ม/กอง)",
        "รองผู้กำกับลูกเสือ(กลุ่ม/กอง)",
        "ผู้ช่วยผู้กำกับลูกเสือ(กลุ่ม/กอง)",
        "วิทยากรลูกเสือ",
        "ผู้ตรวจการลูกเสือ/ผู้ช่วยผู้ตรวจการลูกเสือ",
        "กรรมการลูกเสือ/กรรมการบริหารลูกเสือ",
        "เจ้าหน้าที่ลูกเสือ",
        "อื่น ๆ"
      ],
      scout_qualifications: [
        "ไม่มีคุณวุฒิ",
        "G.I.C. (ขั้นความรู้ทั่วไป)",
        "R.B.T.C. (ขั้นความรู้เบื้องต้น)",
        "R.A.T.C. (ขั้นความรู้ชั้นสูง)",
        "R.W.B. (วูดแบดจ์ 2 ท่อน)",
        "A.L.T.C",
        "A.L.T. (วิทยากร 3 ท่อน)",
        "L.T. (วิทยากร 4 ท่อน)"
      ],
      scout_awards: [
        "รามกีรติฯ",
        "ลูกเสือสดุดี ชั้นที่ 1",
        "ลูกเสือสดุดี ชั้นที่ 2",
        "ลูกเสือสดุดี ชั้นที่ 3",
        "เหรียญลูกเสือยั่งยืน",
        "เหรียญลูกเสือสรรเสริญ ชั้นที่ 1",
        "เหรียญลูกเสือสรรเสริญ ชั้นที่ 2",
        "เหรียญลูกเสือสรรเสริญ ชั้นที่ 3",
        "เครื่องหมายสมนาคุณ ชั้นที่ 1",
        "เครื่องหมายสมนาคุณ ชั้นที่ 2",
        "เครื่องหมายสมนาคุณ ชั้นที่ 3"
      ]
    };
  },

  computed: {
    isSelfMode() {
      return this.sourceType === "self";
    },
    idCard() {
      return (
        this.editpersonnel_temporary.id_card ||
        this.editpersonnel_temporary.personnel_etc_id_card ||
        ""
      );
    },
    countInstructors() {
      return this.personnel_scouts.filter(s => s.scout_instructor === "เป็น")
        .length;
    },
    countWithAwards() {
      return this.personnel_scouts.filter(
        s => this.parseAwards(s.scout_awards).length > 0
      ).length;
    },
    // ใช้ v-model กับ multi-select แล้ว sync กลับเป็น JSON string ตอน submit
    addAwardsModel: {
      get() {
        return this.parseAwards(this.addpersonnel_scout.scout_awards);
      },
      set(val) {
        this.addpersonnel_scout.scout_awards = JSON.stringify(val);
      }
    },
    editAwardsModel: {
      get() {
        return this.parseAwards(this.editpersonnel_scout.scout_awards);
      },
      set(val) {
        this.editpersonnel_scout.scout_awards = JSON.stringify(val);
      }
    }
  },

  async mounted() {
    await this.personnel_scoutsQueryAll();
    if (this.isSelfMode) {
      await this.scoutPeriodStatusQuery();
    }
  },

  methods: {
    async scoutPeriodStatusQuery() {
      try {
        let result = await this.$http.post("period_status.php", {
          ApiKey: this.ApiKey,
          period_type: "scout"
        });
        this.scoutPeriodOpen = !!result.data.period_open;
      } catch (e) {
        this.scoutPeriodOpen = true;
      }
    },
    countByLevel(level) {
      return this.personnel_scouts.filter(item => item.scout_level === level)
        .length;
    },
    levelColor(level) {
      const colors = {
        ลูกเสือสำรอง: "#78909c",
        ลูกเสือสามัญ: "#5c6bc0",
        ลูกเสือสามัญรุ่นใหญ่: "#7e57c2",
        ลูกเสือวิสามัญ: "#2e7d32"
      };
      return colors[level] || "#757575";
    },
    parseAwards(val) {
      if (!val) return [];
      if (Array.isArray(val)) return val;
      try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed : [val];
      } catch {
        return [val];
      }
    },
    formatThaiDate(dateStr) {
      if (!dateStr) return "-";
      const months = [
        "",
        "ม.ค.",
        "ก.พ.",
        "มี.ค.",
        "เม.ย.",
        "พ.ค.",
        "มิ.ย.",
        "ก.ค.",
        "ส.ค.",
        "ก.ย.",
        "ต.ค.",
        "พ.ย.",
        "ธ.ค."
      ];
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return `${d.getDate()} ${months[d.getMonth() + 1]} ${d.getFullYear() +
        543}`;
    },
    showSnackbar(type, text) {
      const configs = {
        success: { color: "#43a047", icon: "mdi-check-circle-outline" },
        error: { color: "#e53935", icon: "mdi-alert-circle-outline" },
        warning: { color: "#fb8c00", icon: "mdi-alert-outline" }
      };
      this.snackbar = { show: true, timeout: 4000, text, ...configs[type] };
    },

    // ===== API =====
    async personnel_scoutsQueryAll() {
      this.loading = true;
      try {
        let result = await this.$http
          .post("personnel_scout.php", {
            ApiKey: this.ApiKey,
            id_card: this.idCard
          })
          .finally(() => (this.loading = false));
        this.personnel_scouts = Array.isArray(result.data) ? result.data : [];
      } catch (e) {
        this.personnel_scouts = [];
      }
    },

    personnel_scoutAdd() {
      this.addpersonnel_scout = { scout_awards: "[]" };
      this.addpersonnel_scoutdialog = true;
    },

    async addpersonnel_scoutSubmit() {
      if (!this.$refs.addpersonnel_scoutform.validate()) return;
      this.addpersonnel_scout.ApiKey = this.ApiKey;
      this.addpersonnel_scout.id_card = this.idCard;
      this.addpersonnel_scout.source_type = this.sourceType;

      let result = await this.$http.post(
        "personnel_scout.insert.php",
        this.addpersonnel_scout
      );
      if (result.data.status === true) {
        this.showSnackbar("success", "เพิ่มข้อมูลลูกเสือวิสามัญเรียบร้อยแล้ว");
        this.personnel_scoutsQueryAll();
      } else {
        this.showSnackbar(
          "error",
          result.data.message || "ไม่สามารถบันทึกข้อมูลได้"
        );
      }
      this.addpersonnel_scoutdialog = false;
    },

    async personnel_scoutEdit(id_red) {
      let result = await this.$http.post("personnel_scout.php", {
        ApiKey: this.ApiKey,
        id_red: id_red
      });
      this.editpersonnel_scout = result.data || {};
      this.editpersonnel_scoutdialog = true;
    },

    async editpersonnel_scoutSubmit() {
      if (!this.$refs.editpersonnel_scoutform.validate()) return;
      this.editpersonnel_scout.ApiKey = this.ApiKey;
      this.editpersonnel_scout.source_type = this.sourceType;

      let result = await this.$http.post(
        "personnel_scout.update.php",
        this.editpersonnel_scout
      );
      if (result.data.status === true) {
        this.showSnackbar("success", "อัปเดตข้อมูลลูกเสือวิสามัญเรียบร้อยแล้ว");
        this.personnel_scoutsQueryAll();
      } else {
        this.showSnackbar(
          "error",
          result.data.message || "ไม่สามารถแก้ไขข้อมูลได้"
        );
      }
      this.editpersonnel_scoutdialog = false;
    },

    async personnel_scoutDelete(id_red) {
      let result = await this.$http.post("personnel_scout.php", {
        ApiKey: this.ApiKey,
        id_red: id_red
      });
      this.editpersonnel_scout = result.data || {};
      this.deletepersonnel_scoutdialog = true;
    },

    async deletepersonnel_scoutSubmit() {
      this.editpersonnel_scout.ApiKey = this.ApiKey;
      this.editpersonnel_scout.source_type = this.sourceType;
      let result = await this.$http.post(
        "personnel_scout.delete.php",
        this.editpersonnel_scout
      );
      if (result.data.status === true) {
        this.showSnackbar("success", "ลบข้อมูลลูกเสือวิสามัญเรียบร้อยแล้ว");
        this.personnel_scoutsQueryAll();
      } else {
        this.showSnackbar(
          "error",
          result.data.message || "ไม่สามารถลบข้อมูลได้"
        );
      }
      this.deletepersonnel_scoutdialog = false;
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Prompt:wght@300;400;500;600;700&display=swap");

.hero-header {
  background: linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%);
  border-radius: 16px;
  overflow: hidden;
}
.hero-content {
  display: flex;
  align-items: center;
  padding: 32px;
}
.hero-icon-wrapper {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 24px;
}
.hero-text {
  color: white;
}
.hero-title {
  font-size: 26px;
  font-weight: 600;
  margin: 0 0 4px 0;
}
.hero-subtitle {
  font-size: 14px;
  opacity: 0.9;
  margin: 0;
  font-weight: 300;
}
.add-btn {
  background: rgba(255, 255, 255, 0.95) !important;
  color: #2e7d32 !important;
  font-weight: 500;
  border-radius: 12px;
  text-transform: none;
}

.stats-row {
  margin-top: -20px;
}
.stat-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  border: 1px solid rgba(46, 125, 50, 0.1);
}
.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.stat-card-1 .stat-icon {
  background: linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%);
}
.stat-card-2 .stat-icon {
  background: linear-gradient(135deg, #388e3c 0%, #1b5e20 100%);
}
.stat-card-3 .stat-icon {
  background: linear-gradient(135deg, #43a047 0%, #2e7d32 100%);
}
.stat-card-4 .stat-icon {
  background: linear-gradient(135deg, #ffb300 0%, #ff8f00 100%);
}
.stat-content {
  display: flex;
  flex-direction: column;
}
.stat-number {
  font-size: 26px;
  font-weight: 700;
  color: #2d3748;
}
.stat-label {
  font-size: 13px;
  color: #718096;
}

.main-table-card {
  background: white;
  border-radius: 16px;
  border: 1px solid rgba(46, 125, 50, 0.08);
}
.table-header {
  padding: 20px 24px;
  background: #f1f8f2;
}
.header-left {
  display: flex;
  align-items: center;
}
.header-title {
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
}

.modern-dialog {
  border-radius: 20px !important;
  overflow: hidden;
}
.dialog-card {
  border-radius: 20px !important;
  overflow: hidden;
}
.dialog-header {
  padding: 20px 24px;
  font-size: 18px;
  font-weight: 600;
  color: white;
}
.add-header {
  background: linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%);
}
.edit-header {
  background: linear-gradient(135deg, #ff8f00 0%, #ff6f00 100%);
}
.delete-header {
  background: linear-gradient(135deg, #e53935 0%, #c62828 100%);
}
.dialog-content {
  padding: 24px !important;
  max-height: 70vh;
  overflow-y: auto;
}

.form-section {
  background: #f1f8f2;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid #c8e6c9;
}
.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #1b5e20;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

.dialog-actions {
  padding: 16px 24px;
  background: #f1f8f2;
}
.cancel-btn {
  color: #718096 !important;
  text-transform: none;
}
.save-btn {
  border-radius: 10px;
  text-transform: none;
}

.delete-content {
  padding: 32px 24px !important;
}
.delete-message {
  font-size: 18px;
  font-weight: 500;
  color: #2d3748;
}
.delete-warning {
  font-size: 13px;
  color: #e53935;
}
.delete-confirm-btn {
  border-radius: 10px;
  text-transform: none;
}

.action-btn {
  width: 32px !important;
  height: 32px !important;
}
.edit-btn {
  background: #fff3e0 !important;
  color: #ff8f00 !important;
}
.delete-btn {
  background: #ffebee !important;
  color: #e53935 !important;
}
</style>
